/**
 * Visit Form Component
 * Create and edit visit with validation
 */

import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Save, X, Calendar, Clock, ClipboardCheck, FileText } from 'lucide-react'

import { useVisit, useCreateVisit, useUpdateVisit } from '@/hooks/useVisits'
import { toast } from '@/lib/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePickerField } from '@/components/ui/date-picker'
import { FormSkeleton } from '@/components/loading'
import { EditVisitClassification } from '@/components/forms/EditVisitClassification'
import { EditVisitTransaction } from '@/components/forms/EditVisitTransaction'

// Validation schema
const visitSchema = z.object({
  client_id: z.string().uuid('Invalid client ID'),
  visit_date: z.string().optional().nullable().or(z.literal('')),
  time_in: z.string().optional().nullable().or(z.literal('')),
  time_out: z.string().optional().nullable().or(z.literal('')),
  office_id: z.number().optional().nullable(),
  status: z.number().optional().nullable(),
})

type VisitFormData = z.infer<typeof visitSchema>

export function VisitForm() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id

  // Fetch existing visit if editing
  const { data: visit, isLoading: isLoadingVisit } = useVisit(id!, {
    enabled: isEditing,
  })

  // Mutations
  const createVisit = useCreateVisit()
  const updateVisit = useUpdateVisit()

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<VisitFormData>({
    resolver: zodResolver(visitSchema),
    defaultValues: {
      client_id: '',
      visit_date: '',
      time_in: '',
      time_out: '',
      office_id: undefined,
      status: 1, // Default to Scheduled
    },
  })

  // Load existing visit data
  useEffect(() => {
    if (visit) {
      setValue('client_id', visit.client_id || '')
      setValue('visit_date', visit.visit_date || '')
      setValue('time_in', visit.time_in || '')
      setValue('time_out', visit.time_out || '')
      setValue('office_id', visit.office_id || undefined)
      setValue('status', visit.status || undefined)
    }
  }, [visit, setValue])

  // Submit handler
  const onSubmit = async (data: VisitFormData) => {
    try {
      // Convert empty strings to null
      const cleanData = Object.fromEntries(
        Object.entries(data).map(([key, value]) =>
          [key, value === '' ? null : value]
        )
      ) as any

      if (isEditing) {
        await updateVisit.mutateAsync({ id: id!, data: cleanData })
        toast.success('Visit updated successfully!')
      } else {
        await createVisit.mutateAsync(cleanData)
        toast.success('Visit created successfully!')
      }
      navigate('/visits')
    } catch (error: any) {
      toast.error(
        isEditing ? 'Failed to update visit' : 'Failed to create visit',
        error.response?.data?.detail || error.message
      )
    }
  }

  if (isLoadingVisit) {
    return <FormSkeleton fields={7} />
  }

  const status = watch('status')

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-primary">
          {isEditing ? 'Edit Visit' : 'New Visit'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isEditing
            ? 'Update visit information below'
            : 'Fill in the form to create a new visit'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Client Information */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5" />
            Visit Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Client ID */}
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="client_id">
                Client ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="client_id"
                {...register('client_id')}
                placeholder="Enter client UUID"
                className={errors.client_id ? 'border-red-500' : ''}
                disabled={isEditing}
              />
              {errors.client_id && (
                <p className="text-sm text-red-500">{errors.client_id.message}</p>
              )}
              <p className="text-xs text-gray-500">
                UUID format: 123e4567-e89b-12d3-a456-426614174000
              </p>
            </div>

            {/* Visit Date */}
            <div className="space-y-2">
              <Label htmlFor="visit_date">Visit Date</Label>
              <DatePickerField
                value={watch('visit_date')}
                onChange={(value) => setValue('visit_date', value)}
                placeholder="Select visit date"
              />
            </div>

            {/* Office ID */}
            <div className="space-y-2">
              <Label htmlFor="office_id">Office ID</Label>
              <Input
                type="number"
                id="office_id"
                {...register('office_id', { valueAsNumber: true })}
                placeholder="Enter office ID"
              />
            </div>
          </div>
        </div>

        {/* Time Information */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
            <Clock className="h-5 w-5" />
            Time Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Time In */}
            <div className="space-y-2">
              <Label htmlFor="time_in">Time In</Label>
              <Input
                type="time"
                id="time_in"
                {...register('time_in')}
              />
            </div>

            {/* Time Out */}
            <div className="space-y-2">
              <Label htmlFor="time_out">Time Out</Label>
              <Input
                type="time"
                id="time_out"
                {...register('time_out')}
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold text-primary mb-4">Status</h2>
          <div className="space-y-2">
            <Label htmlFor="status">Visit Status</Label>
            <Select
              value={status?.toString() || ''}
              onValueChange={(value) => setValue('status', value ? Number(value) : null)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Scheduled</SelectItem>
                <SelectItem value="2">In Progress</SelectItem>
                <SelectItem value="3">Completed</SelectItem>
                <SelectItem value="4">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/visits')}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? 'Saving...' : isEditing ? 'Update Visit' : 'Create Visit'}
          </Button>
        </div>
      </form>

      {/* Classification (edited independently from the visit's own fields) */}
      {isEditing && visit && visit.classifications && visit.classifications.length > 0 && (
        <div className="bg-white rounded-lg border p-6 mt-8">
          <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
            <ClipboardCheck className="h-5 w-5" />
            Classification
          </h2>
          <EditVisitClassification visitId={id!} classification={visit.classifications[0]} />
        </div>
      )}

      {/* Assistance Details (edited independently per transaction) */}
      {isEditing && visit && visit.transactions && visit.transactions.length > 0 && (
        <div className="bg-white rounded-lg border p-6 mt-8">
          <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5" />
            Assistance Details
          </h2>
          <div className="space-y-6">
            {visit.transactions.map((transaction) => (
              <EditVisitTransaction key={transaction.id} transaction={transaction} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default VisitForm
